'use client';

import { useEffect, useState } from 'react';
import { Check, X, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  onClose?: () => void;
}

const ICON = {
  success: { Icon: Check, tone: 'text-positive' },
  error: { Icon: AlertTriangle, tone: 'text-critical' },
  info: { Icon: Info, tone: 'text-informative' },
} as const;

/** Neutral toast (DESIGN.md §6): surface, hairline, tone only on the icon. */
export function Toast({ message, type = 'success', duration = 3000, onClose }: ToastProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onClose?.(), 200);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const { Icon, tone } = ICON[type];

  return (
    <div
      className={cn(
        // `end-4`: the shell flips to RTL for Arabic; a logical inset follows it.
        'fixed bottom-4 end-4 z-50 transition-[opacity,transform] duration-200',
        visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'
      )}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <div className="flex items-center gap-3 rounded-card border border-line bg-surface py-2.5 pe-2 ps-3 text-ink shadow-[0_12px_32px_rgb(0_0_0/0.35)] [[data-theme=light]_&]:shadow-[0_12px_32px_rgb(0_0_0/0.12)]">
        <Icon className={cn('size-4 shrink-0', tone)} aria-hidden="true" />
        <span className="text-sm">{message}</span>
        <button
          type="button"
          onClick={() => {
            setVisible(false);
            onClose?.();
          }}
          aria-label="Dismiss notification"
          className="ms-1 rounded-[6px] p-1 text-ink-2 hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** Single-toast state for a component. */
export function useToast() {
  const [toast, setToast] = useState<ToastProps | null>(null);

  const showToast = (message: string, type: ToastProps['type'] = 'success', duration?: number) => {
    setToast({ message, type, duration });
  };

  const hideToast = () => setToast(null);

  return { toast, showToast, hideToast };
}
